import type { LayerSpecification, StyleSpecification } from "maplibre-gl";
import cloneDeep from "lodash.clonedeep";
import type { ColorPath, Role } from "./types";

export type AnyLayer = LayerSpecification & {
  paint?: { [prop: string]: any };
  layout?: { [prop: string]: any };
};

export function roleOf(prop: string): Role {
  if (prop.endsWith("halo-color")) return "halo";
  if (prop === "text-color" || prop === "icon-color") return "text";
  return "shape";
}

export function layerById(
  style: StyleSpecification,
  id: string
): AnyLayer | undefined {
  return (style.layers || []).find((layer) => layer.id === id) as
    | AnyLayer
    | undefined;
}

// The value a path points at, or undefined when the paint has another shape.
export function readPath(layer: AnyLayer | undefined, target: ColorPath): unknown {
  let value: any = layer?.paint?.[target.prop];
  for (const step of target.path) {
    if (value === null || typeof value !== "object") return undefined;
    value = value[step];
  }
  return value;
}

// Returns a new style with the colour at every path replaced by
// `change(colour)`. A path that does not lead to a string in a layer's paint
// (the style was edited elsewhere, or two layers share an id) is skipped;
// a layer in which nothing was written is returned as it was.
export function rewrite<S extends StyleSpecification>(
  style: S,
  paths: ColorPath[],
  change: (color: string) => string
): S {
  const byLayer = new Map<string, ColorPath[]>();
  paths.forEach((target) => {
    byLayer.set(target.layerId, [...(byLayer.get(target.layerId) || []), target]);
  });

  const layers = style.layers.map((layer) => {
    const targets = byLayer.get(layer.id);
    if (!targets) return layer;
    const paint = cloneDeep((layer as AnyLayer).paint || {});
    let written = false;
    targets.forEach((target) => {
      const current = readPath({ ...layer, paint } as AnyLayer, target);
      if (typeof current !== "string") return;
      const next = change(current);
      written = true;
      if (target.path.length === 0) {
        paint[target.prop] = next;
        return;
      }
      let node = paint[target.prop];
      target.path.slice(0, -1).forEach((step) => {
        node = node[step];
      });
      node[target.path[target.path.length - 1]] = next;
    });
    return written ? ({ ...layer, paint } as LayerSpecification) : layer;
  });

  return { ...style, layers };
}

export type ZoomSide = "out" | "in";

// Index of the zoomed-out and zoomed-in output in a two-stop zoom blend:
// ["interpolate", <type>, ["zoom"], <zoom>, <out>, <zoom>, <in>].
export const ZOOM_SIDE_INDEX: { [side in ZoomSide]: number } = { out: 4, in: 6 };

export function isZoomBlend(value: unknown): value is unknown[] {
  return (
    Array.isArray(value) &&
    value.length === 7 &&
    typeof value[0] === "string" &&
    value[0].startsWith("interpolate") &&
    Array.isArray(value[2]) &&
    value[2].length === 1 &&
    value[2][0] === "zoom" &&
    typeof value[3] === "number" &&
    typeof value[5] === "number"
  );
}

// Which end of a two-stop zoom blend a colour sits in, or null when its
// value does not change with zoom that way.
export function zoomSideOf(
  style: StyleSpecification,
  target: ColorPath
): ZoomSide | null {
  const value = layerById(style, target.layerId)?.paint?.[target.prop];
  if (!isZoomBlend(value)) return null;
  if (target.path[0] === ZOOM_SIDE_INDEX.out) return "out";
  if (target.path[0] === ZOOM_SIDE_INDEX.in) return "in";
  return null;
}
